import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import MaturityDisclaimer from '../../components/MaturityDisclaimer';
import Modal from '../../components/Modal';
import { clientApi } from '../../services/client';
import type { SiteListItem } from '../../types';
import extractApiError from '../../utils/extractApiError';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

export default function ClientVendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const { data: vendor, isLoading, isError } = useQuery({
    queryKey: ['client', 'vendors', id],
    queryFn: () => clientApi.getVendor(id!).then((r) => r.data),
    enabled: !!id,
  });

  // Fetch sites serviced by this vendor (filtered server-side)
  const { data: sitesData } = useQuery({
    queryKey: ['client', 'sites', 'vendor', id],
    queryFn: () => clientApi.getSites({ page: '1', vendor_org: id! }).then((r) => r.data),
    enabled: !!id,
  });

  const cancelMutation = useMutation({
    mutationFn: () => clientApi.cancelVendor(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client'] });
      setShowCancelModal(false);
      setToast({ message: 'Vendor relationship cancelled.', type: 'success' });
      setTimeout(() => navigate('/client/vendors'), 2000);
    },
    onError: (err: unknown) => {
      setShowCancelModal(false);
      setToast({ message: extractApiError(err), type: 'error' });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !vendor) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">{isError ? 'Failed to load vendor.' : 'Vendor not found'}</p>
      </div>
    );
  }

  const vendorSites = sitesData?.results ?? [];

  return (
    <>
      <PageHeader
        title={vendor.name}
        description={`${vendor.site_count} site${vendor.site_count !== 1 ? 's' : ''} under management`}
        actions={
          <button
            onClick={() => navigate('/client/vendors')}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Back to Vendors
          </button>
        }
      />

      {toast && (
        <div className={`mb-4 rounded-xl border px-4 py-3 ${toast.type === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
          <p className={`text-sm ${toast.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{toast.message}</p>
        </div>
      )}

      {/* Feature 3: maturity disclaimer when this vendor hasn't passed the publish gate */}
      <MaturityDisclaimer
        when={!!vendor.publish_status && !vendor.publish_status.is_publishable}
      />

      {/* Score + Info row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <ScoreCard label="Vendor Score" score={vendor.score} size="lg" />
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Vendor Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Company Name</dt>
              <dd className="mt-1 text-sm text-slate-900">{vendor.name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Phone</dt>
              <dd className="mt-1 text-sm text-slate-900">{vendor.phone || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Website</dt>
              <dd className="mt-1 text-sm text-slate-900">
                {vendor.website ? (
                  <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
                    {vendor.website}
                  </a>
                ) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-400 uppercase">Sites Managed</dt>
              <dd className="mt-1 text-sm text-slate-900">{vendor.site_count}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* About Section */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">About</h2>
        <p className="text-sm text-slate-500">No description available yet.</p>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 mb-8">
        <button
          onClick={() => navigate(`/client/sites`)}
          className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 rounded-xl hover:bg-brand-100"
        >
          View Sites
        </button>
        <button
          onClick={() => setShowCancelModal(true)}
          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100"
        >
          Cancel Vendor
        </button>
      </div>

      {/* Score Trend Placeholder */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Score Trend</h2>
        <div className="flex items-center justify-center h-48 bg-slate-50 rounded-xl">
          <p className="text-sm text-slate-400">Score trend chart will appear here after weekly scoring runs</p>
        </div>
      </div>

      {/* Sites serviced by this vendor */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Sites ({vendorSites.length})
        </h2>
        {vendorSites.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Site Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Guards</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vendorSites.map((site: SiteListItem) => (
                  <tr
                    key={site.id}
                    className="cursor-pointer hover:bg-blue-50/50"
                    onClick={() => navigate(`/client/sites/${site.id}`)}
                  >
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{site.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-500">{site.city}, {site.state}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`font-semibold ${getScoreColorClass(getScoreColor(site.score))}`}>
                        {toGGScore(site.score)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{site.guard_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No sites currently assigned to this vendor.</p>
        )}
      </div>

      {/* Cancel Vendor Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Vendor Relationship" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Are you sure you want to cancel your relationship with <strong>{vendor.name}</strong>?
          </p>
          <div className="rounded-xl bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-700 font-medium">This will:</p>
            <ul className="mt-1 text-sm text-red-600 list-disc list-inside space-y-1">
              <li>Remove the vendor from all your sites</li>
              <li>Deactivate all guard assignments at those sites</li>
              <li>Cancel the vendor connection</li>
            </ul>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50"
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Yes, Cancel Vendor'}
            </button>
            <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">
              Keep Vendor
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
